import { lazy, Suspense, useEffect, useMemo, useState } from 'react';
import type { ComponentType } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Avatar, Button, Dropdown, Layout, Menu, Result, Spin } from 'antd';
import {
  Activity, BookOpenCheck, Building2, ChevronLeft, ChevronRight, CircleGauge,
  Cable, ClipboardList, Database, FileClock, PhoneCall, Factory,
  LogOut, Server, Settings, ShieldCheck, Trash2, Users
} from 'lucide-react';
import { Navigate, Route, useLocation } from 'react-router-dom';
import { api, type AdminSession, logout, refreshCsrf } from './api';
import LoginPage from './LoginPage';
import { AdminWorkspace, AdminWorkspaceProvider, clearWorkspaceStorage, useAdminWorkspace } from './AdminWorkspace';

const pages = import('./pages');
const lazyPage = <T extends keyof Awaited<typeof pages>>(name: T) => lazy(async () => ({ default: (await pages)[name] as ComponentType<any> }));
const AdminAccountsPage = lazyPage('AdminAccountsPage');
const AttendancePage = lazyPage('AttendancePage');
const AuditPage = lazyPage('AuditPage');
const DashboardPage = lazyPage('DashboardPage');
const HardwareAccessPage = lazyPage('HardwareAccessPage');
const MigrationsPage = lazyPage('MigrationsPage');
const MonitoringPage = lazyPage('MonitoringPage');
const OrdersPage = lazyPage('OrdersPage');
const PeoplePage = lazyPage('PeoplePage');
const SchoolsPage = lazyPage('SchoolsPage');
const StudentManagementPage = lazyPage('StudentManagementPage');
const SystemPage = lazyPage('SystemPage');
const CardProductionPage = lazyPage('CardProductionPage');
const CallSubscriptionsPage = lazyPage('CallSubscriptionsPage');
const EducationBureauDisplayPage = lazy(() => import('./EducationBureauDisplayPage'));
const SchoolDisplayPage = lazy(() => import('./SchoolDisplayPage'));
const DeletionCenterPage = lazy(() => import('./deletion').then((module) => ({ default: module.DeletionCenterPage })));

const { Header, Sider } = Layout;

const routes = [
  { key: '/', label: '平台概览', icon: CircleGauge },
  { key: '/schools', label: '学校管理', icon: Building2 },
  { key: '/people', label: '人员与身份', icon: Users },
  { key: '/students', label: '学生管理', icon: BookOpenCheck },
  { key: '/deletions', label: '删除与保留', icon: Trash2, technical: true },
  { key: '/card-production', label: '制卡中心', icon: Factory },
  { key: '/call-subscriptions', label: '通话服务', icon: PhoneCall },
  { key: '/hardware-access', label: '设备与硬件', icon: Cable },
  { key: '/attendance', label: '考勤管理', icon: Activity },
  { key: '/monitoring', label: '服务器监控', icon: Server, technical: true },
  { key: '/migrations', label: '迁移对账', icon: Database, technical: true },
  { key: '/orders', label: '订单管理', icon: ClipboardList },
  { key: '/audit', label: '审计日志', icon: FileClock },
  { key: '/settings', label: '系统设置', icon: Settings, technical: true }
];

export default function App() {
  const location = useLocation();
  const queryClient = useQueryClient();
  const shareDemo = import.meta.env.VITE_BUREAU_SHARE_DEMO === 'true';
  const previewDisplay = location.pathname === '/bureau-display/preview'
    && ['127.0.0.1', 'localhost', 'terminal.local'].includes(window.location.hostname);
  const publicEducationDisplay = shareDemo || location.pathname === '/education-display';
  const schoolDisplayMatch = location.pathname.match(/^\/school-display\/([^/]+)$/);
  const schoolDisplayId = schoolDisplayMatch?.[1] || '';
  const publicSchoolDisplayMatch = location.pathname.match(/^\/school-display\/public\/([^/]+)$/);
  const publicSchoolDisplayId = publicSchoolDisplayMatch?.[1] || '';
  const previewSchoolDisplay = schoolDisplayId === 'preview'
    && ['127.0.0.1', 'localhost', 'terminal.local'].includes(window.location.hostname);
  const [session, setSession] = useState<AdminSession | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (previewDisplay || publicEducationDisplay || previewSchoolDisplay || publicSchoolDisplayId) {
      setLoading(false);
      return;
    }
    api<AdminSession>('/auth/me')
      .then(async (value) => {
        if (!value.csrfSessionId) throw new Error('后台会话信息不完整，请重新登录');
        // Bind the profile and CSRF fetch before showing any account-specific forms.
        await refreshCsrf(value.csrfSessionId);
        setSession(value);
      })
      .catch(() => {
        queryClient.clear();
        clearWorkspaceStorage();
        setSession(null);
      })
      .finally(() => setLoading(false));
    const expired = () => {
      // Stop cached list observers from retrying protected images after the session is gone.
      queryClient.clear();
      clearWorkspaceStorage();
      setSession(null);
    };
    window.addEventListener('admin-session-expired', expired);
    return () => window.removeEventListener('admin-session-expired', expired);
  }, [previewDisplay, publicEducationDisplay, previewSchoolDisplay, publicSchoolDisplayId, queryClient]);

  if (previewSchoolDisplay) return <Suspense fallback={<div className="app-loader"><Spin size="large" /></div>}><SchoolDisplayPage preview schoolId="preview" /></Suspense>;
  if (publicSchoolDisplayId) return <Suspense fallback={<div className="app-loader"><Spin size="large" /></div>}><SchoolDisplayPage publicDisplayId={publicSchoolDisplayId} /></Suspense>;
  if (publicEducationDisplay) return <Suspense fallback={<div className="app-loader"><Spin size="large" /></div>}><EducationBureauDisplayPage publicDisplay /></Suspense>;
  if (previewDisplay) return <Suspense fallback={<div className="app-loader"><Spin size="large" /></div>}><EducationBureauDisplayPage preview session={{ adminId: 'preview', username: 'preview', displayName: '展示预览', role: 'PLATFORM_ADMIN' }} /></Suspense>;
  if (loading) return <div className="app-loader"><Spin size="large" /></div>;
  if (!session) return <LoginPage onLogin={setSession} />;
  if (schoolDisplayId) return <Suspense fallback={<div className="app-loader"><Spin size="large" /></div>}><SchoolDisplayPage schoolId={schoolDisplayId} /></Suspense>;
  if (location.pathname === '/bureau-display') return <Suspense fallback={<div className="app-loader"><Spin size="large" /></div>}><EducationBureauDisplayPage session={session} /></Suspense>;
  return <AdminShell session={session} onLogout={() => setSession(null)} />;
}

function AdminShell({ session, onLogout }: { session: AdminSession; onLogout: () => void }) {
  const allowed = routes.filter(item => session.role === 'REGIONAL_REGULATOR' ? item.key === '/' : !item.technical || ['PLATFORM_ADMIN', 'DEVELOPER', 'AUDITOR'].includes(session.role));
  const owner = `${session.adminId}:${session.csrfSessionId}:${session.role}`;
  return <AdminWorkspaceProvider key={owner} owner={owner} routes={allowed}><AdminShellView session={session} onLogout={onLogout}/></AdminWorkspaceProvider>;
}

function AdminShellView({ session, onLogout }: { session: AdminSession; onLogout: () => void }) {
  const workspace = useAdminWorkspace();
  const [collapsed, setCollapsed] = useState(false);
  const location = useLocation();
  const queryClient = useQueryClient();
  const regionalRegulator = session.role === 'REGIONAL_REGULATOR';
  const technical = ['PLATFORM_ADMIN', 'DEVELOPER', 'AUDITOR'].includes(session.role);
  const visibleRoutes = useMemo(() => regionalRegulator
    ? routes.filter((item) => item.key === '/')
    : routes.filter((item) => !item.technical || technical), [regionalRegulator, technical]);
  const selectedRoute = visibleRoutes.find((item) => item.key !== '/' && (location.pathname === item.key || location.pathname.startsWith(`${item.key}/`)))
    || visibleRoutes.find((item) => item.key === location.pathname)
    || visibleRoutes[0];
  const signOut = async () => {
    if (!await workspace.mayLeave(workspace.tabs.map(tab => tab.key))) return;
    try { await logout(); } finally {
      queryClient.clear();
      clearWorkspaceStorage();
      onLogout();
    }
  };

  return (
    <Layout className="admin-layout">
      <Sider width={184} collapsedWidth={72} collapsed={collapsed} className="admin-sider" trigger={null}>
        <div className={`brand ${collapsed ? 'brand-collapsed' : ''}`}>
          <img src="/yunjing-logo-mark.png" alt="云菁家校" />
          {!collapsed && <div><strong>云菁家校</strong><span>平台运营控制台</span></div>}
        </div>
        <Menu
          theme="dark"
          mode="inline"
          selectedKeys={[selectedRoute.key]}
          items={visibleRoutes.map(({ key, label, icon: Icon }) => ({ key, label, icon: <Icon size={19} /> }))}
          onClick={({ key }) => workspace.visit(key)}
        />
        <Button className="collapse-button" type="text" onClick={() => setCollapsed((value) => !value)}>
          {collapsed ? <ChevronRight size={18} /> : <><ChevronLeft size={18} /><span>收起</span></>}
        </Button>
      </Sider>
      <Layout>
        <Header className="admin-header">
          <div className="workspace-label">运营工作台</div>
          <div className="header-meta">
            <span className="environment"><i />后台服务</span>
            <span className="online"><ShieldCheck size={16} />在线</span>
            <Dropdown trigger={['click']} menu={{ items: [
              { key: 'account', label: `${session.displayName} · ${roleText(session.role)}`, disabled: true },
              { type: 'divider' },
              { key: 'logout', label: '退出登录', icon: <LogOut size={16} />, onClick: signOut }
            ] }}>
              <Button type="text" className="account-button">
                <Avatar size={30}>{session.displayName.slice(0, 1)}</Avatar>
                <span>{session.displayName}</span>
              </Button>
            </Dropdown>
          </div>
        </Header>
        <AdminWorkspace>
            <Route path="/" element={<DashboardPage session={session} />} />
            <Route path="/schools" element={regionalRegulator ? <Forbidden /> : <SchoolsPage session={session} />} />
            <Route path="/people" element={regionalRegulator ? <Forbidden /> : <PeoplePage session={session} />} />
            <Route path="/identities" element={<Navigate to="/people?tab=identities" replace />} />
            <Route path="/teachers" element={<Navigate to="/people?tab=teachers" replace />} />
            <Route path="/students" element={regionalRegulator ? <Forbidden /> : <StudentManagementPage session={session} />} />
            <Route path="/deletions" element={technical ? <DeletionCenterPage session={session} /> : <Forbidden />} />
            <Route path="/cards" element={<Navigate to="/students" replace />} />
            <Route path="/card-production" element={regionalRegulator ? <Forbidden /> : <CardProductionPage session={session} />} />
            <Route path="/enrollment-qr" element={<Navigate to="/schools?tab=qrs" replace />} />
            <Route path="/call-subscriptions" element={regionalRegulator ? <Forbidden /> : <CallSubscriptionsPage session={session} />} />
            <Route path="/devices" element={<Navigate to="/hardware-access?tab=devices" replace />} />
            <Route path="/hardware-access" element={regionalRegulator ? <Forbidden /> : <HardwareAccessPage session={session} />} />
            <Route path="/attendance-hardware" element={<Navigate to="/hardware-access?tab=attendance" replace />} />
            <Route path="/call-terminal-hardware" element={<Navigate to="/hardware-access?tab=phone" replace />} />
            <Route path="/attendance" element={regionalRegulator ? <Forbidden /> : <AttendancePage />} />
            <Route path="/monitoring" element={technical ? <MonitoringPage /> : <Forbidden />} />
            <Route path="/migrations" element={technical ? <MigrationsPage /> : <Forbidden />} />
            <Route path="/orders" element={regionalRegulator ? <Forbidden /> : <OrdersPage />} />
            <Route path="/audit" element={regionalRegulator ? <Forbidden /> : <AuditPage />} />
            <Route path="/settings" element={technical ? <SystemPage session={session} /> : <Forbidden />} />
            <Route path="/settings/accounts" element={session.role === 'PLATFORM_ADMIN' ? <AdminAccountsPage /> : <Forbidden />} />
            <Route path="*" element={<Navigate to="/" replace />} />
        </AdminWorkspace>
      </Layout>
    </Layout>
  );
}

const roleText = (role: string) => ({
  PLATFORM_ADMIN: '平台管理员', OPERATIONS: '运营人员', DEVELOPER: '开发运维', AUDITOR: '只读审计', REGIONAL_REGULATOR: '教育监管'
}[role] || role);

const Forbidden = () => <Result status="403" title="无权访问" subTitle="当前后台账号没有此功能权限。" />;
